import type { ReactNode } from 'react';

import { useIsPageEditor } from '@/hooks/use-is-page-editor';
import type {
  CollectionCardAlign,
  CollectionGap,
  CollectionImagePosition,
} from '@/lib/blocks/collection/blocks';
import {
  getActiveMediaField,
  getCardContentGapClassName,
  getContentFields,
  getEnabledFields,
  getImageField,
} from '@/lib/blocks/collection/blocks';
import { resolveCollectionMediaLayout } from '@/lib/blocks/collection/media-layout';
import { readImageUrl, readImageValue, readString } from '@/lib/prop-utils';
import { cn } from '@/lib/utils';
import { getImageUrl, isPendingAIImage } from '@/types/image';
import { ImageWithLoading } from '../section-image/image-with-loading';
import { CollectionMediaFrame } from './collection-media-frame';
import { SideBySideCard } from './side-by-side-card';
import type { CollectionCardProps, RenderField } from './types';

export function renderCollectionFieldSlot(
  field: Parameters<RenderField>[0],
  item: CollectionCardProps['item'],
  renderField: RenderField,
  className?: string,
) {
  const content = renderField(field, item[field.slot]);
  if (!content) return null;

  return (
    <div key={field.slot} className={cn('w-full empty:hidden', className)}>
      {content}
    </div>
  );
}

export type CollectionCardColumnProps = {
  gap: CollectionGap;
  align: CollectionCardAlign;
  paddingClassName?: string;
  className?: string;
  children: ReactNode;
};

export function CollectionCardColumn({
  gap,
  align,
  paddingClassName,
  className,
  children,
}: CollectionCardColumnProps) {
  return (
    <div
      className={cn(
        'flex flex-col',
        getCardContentGapClassName(gap),
        paddingClassName,
        className,
        align === 'center' && 'items-center text-center',
        align === 'end' && 'items-end text-right',
      )}
    >
      {children}
    </div>
  );
}

export function alternatingImagePosition(rowIndex: number): CollectionImagePosition {
  return rowIndex % 2 === 1 ? 'left' : 'right';
}

export function CollectionCard({
  item,
  cardConfig,
  mediaLayout,
  sideBySideMode = 'container',
  paddingClassName,
  contentPaddingClassName,
  renderField,
  renderImageField,
}: CollectionCardProps) {
  const isPageEditor = useIsPageEditor();
  const enabledFields = getEnabledFields(cardConfig.fields);
  if (enabledFields.length === 0) return null;

  const mediaField = getActiveMediaField(enabledFields);
  const imageField = getImageField(enabledFields);
  const contentFields = getContentFields(enabledFields);
  const requestedMediaLayout = mediaLayout ?? {
    position: 'top' as const,
    size: 'auto' as const,
  };
  const resolvedMediaLayout = resolveCollectionMediaLayout({
    layout: requestedMediaLayout,
    mediaField: mediaField ?? undefined,
    mediaValue: mediaField ? item[mediaField.slot] : undefined,
    cardAlign: cardConfig.align,
  });
  const rawImage = imageField ? item[imageField.slot] : undefined;
  const imageValue = imageField ? readImageValue(rawImage) : undefined;
  const imageUrl = imageField ? (getImageUrl(imageValue) ?? readImageUrl(rawImage)) : undefined;
  const hasImageData = Boolean(imageUrl) || isPendingAIImage(imageValue);
  if (resolvedMediaLayout.position === 'background' && imageField) {
    const bgMinHeight = cardConfig.minHeight ?? 320;
    const imageShape = readString(imageField.blockProps?.shape);
    const panel = cardConfig.contentPanel;
    const hasPanel = Boolean(panel);
    const isDarkPanel = panel === 'dark';
    return (
      <div
        className={cn(
          'relative flex flex-col overflow-hidden bg-gray-400',
          imageShape !== 'sharp' && 'rounded-lg',
          cardConfig.justify === 'start' && 'justify-start',
          cardConfig.justify === 'center' && 'justify-center',
          (!cardConfig.justify || cardConfig.justify === 'end') && 'justify-end',
        )}
        style={{ minHeight: `${bgMinHeight}px` }}
      >
        {hasImageData ? (
          <div className="absolute inset-0">
            <ImageWithLoading
              image={imageValue ?? { url: imageUrl }}
              alt=""
              className="h-full w-full object-cover"
              inlineSvg={false}
            />
          </div>
        ) : null}
        {!hasPanel && <div className="absolute inset-0 bg-black/40" />}
        <div
          className={cn(
            'relative z-10 flex flex-col p-6 text-white',
            !hasPanel && cardConfig.contentPadding && 'w-full max-w-4xl sm:px-14 sm:py-12 lg:px-16',
            !hasPanel &&
              cardConfig.contentPadding &&
              cardConfig.align === 'center' &&
              'self-center',
            !hasPanel && cardConfig.contentPadding && cardConfig.align === 'end' && 'self-end',
            contentPaddingClassName,
            getCardContentGapClassName(cardConfig.gap),
            hasPanel && 'py-8 max-w-xl',
            hasPanel && cardConfig.align === 'end' && 'self-end mr-6 sm:mr-12',
            hasPanel && cardConfig.align === 'center' && 'self-center',
            hasPanel &&
              cardConfig.align !== 'end' &&
              cardConfig.align !== 'center' &&
              'self-start ml-6 sm:ml-12',
            isDarkPanel && 'bg-black/70',
            cardConfig.align === 'center' && 'items-center text-center',
            cardConfig.align === 'end' && 'items-end text-right',
          )}
          style={hasPanel && !isDarkPanel ? { backgroundColor: panel } : undefined}
        >
          {contentFields.map((field) =>
            renderCollectionFieldSlot(
              field,
              item,
              renderField,
              cn(
                '[&_*]:!text-white',
                cardConfig.align === 'end' && 'text-right',
                cardConfig.align === 'center' && 'text-center',
              ),
            ),
          )}
        </div>
      </div>
    );
  }

  const fieldColumnProps = {
    gap: cardConfig.gap,
    align: cardConfig.align,
    paddingClassName,
  } satisfies Pick<CollectionCardColumnProps, 'gap' | 'align' | 'paddingClassName'>;

  const hideEmptyImage = cardConfig.sourceMode === 'dynamic' && !hasImageData;
  const hasMediaData =
    mediaField?.kind === 'image' ? hasImageData : Boolean(mediaField && item[mediaField.slot]);
  const mediaContent =
    mediaField && !hideEmptyImage && (isPageEditor || hasMediaData)
      ? (renderImageField ?? renderField)(mediaField, item[mediaField.slot])
      : null;
  const media =
    mediaField && mediaContent ? (
      <CollectionMediaFrame key={mediaField.slot} layout={resolvedMediaLayout}>
        {mediaContent}
      </CollectionMediaFrame>
    ) : null;

  if (resolvedMediaLayout.position === 'top' || !mediaField || !media) {
    return (
      <CollectionCardColumn {...fieldColumnProps}>
        {enabledFields.map((field) =>
          field === mediaField
            ? media
            : renderCollectionFieldSlot(field, item, renderField, contentPaddingClassName),
        )}
      </CollectionCardColumn>
    );
  }

  const image = (className: string) => (
    <CollectionMediaFrame
      layout={resolvedMediaLayout}
      className={cn(className, contentPaddingClassName)}
    >
      {mediaContent}
    </CollectionMediaFrame>
  );

  const content = (
    <CollectionCardColumn {...fieldColumnProps} className={cn('w-full', contentPaddingClassName)}>
      {contentFields.map((field) => renderCollectionFieldSlot(field, item, renderField))}
    </CollectionCardColumn>
  );

  return (
    <div className="@container h-full min-w-0">
      <SideBySideCard
        imagePosition={resolvedMediaLayout.position}
        mode={sideBySideMode}
        gap={cardConfig.gap}
        image={image}
        content={content}
      />
    </div>
  );
}
